import axios from 'axios';
import { env } from '@/config/env';
import { API_ENDPOINTS } from '../api/endpoints';
import { TokenStorage } from './tokenStorage';
import type { TokenRefreshResponse } from './tokenTypes';

/**
 * Token service for handling token refresh
 */

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class TokenService {
  /**
   * Refresh access token using the refresh token
   * Uses the /token/refresh endpoint with Authorization header
   *
   * @returns Promise<string | null> New access token or null if refresh failed
   */
  static async refreshAccessToken(): Promise<string | null> {
    // If already refreshing, return the existing promise
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    isRefreshing = true;

    refreshPromise = this.performTokenRefresh();

    try {
      return await refreshPromise;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh request
   */
  private static async performTokenRefresh(): Promise<string | null> {
    // Try new token storage first
    const tokens = TokenStorage.getTokens();

    if (tokens) {
      const result = await this.refreshWithTokenStorage(tokens);
      if (result) {
        return result;
      }
    }

    // Fallback to legacy mpn_refresh_token
    const legacyRefreshToken = localStorage.getItem('mpn_refresh_token');
    if (legacyRefreshToken) {
      console.warn('Attempting refresh with legacy mpn_refresh_token');
      const result = await this.refreshWithLegacyToken(legacyRefreshToken);
      if (result) {
        return result;
      }
    }

    console.warn('No tokens available for refresh');
    return null;
  }

  /**
   * Refresh using TokenStorage tokens (new system with expiration times)
   */
  private static async refreshWithTokenStorage(
    tokens: NonNullable<ReturnType<typeof TokenStorage.getTokens>>
  ): Promise<string | null> {
    try {
      // Check if refresh token is expired
      if (TokenStorage.isRefreshTokenExpired()) {
        console.warn('Refresh token is expired');
        TokenStorage.clearTokens();
        return null;
      }

      const { refreshToken } = tokens;

      // POST /token/refresh: no request body per API spec
      // Refresh token sent via Authorization header (AdminRefreshAuthorizer)
      const response = await axios.post<TokenRefreshResponse>(
        `${env.VITE_API_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
        undefined,
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Store the new tokens
      const newTokens = response.data;
      TokenStorage.setTokens(newTokens);

      // Also update legacy storage for consistency
      localStorage.setItem('mpn_access_token', newTokens.accessToken);
      localStorage.setItem('mpn_refresh_token', newTokens.refreshToken);

      return newTokens.accessToken;
    } catch (error) {
      console.error('Token refresh with TokenStorage failed:', error);
      TokenStorage.clearTokens();
      return null;
    }
  }

  /**
   * Refresh using legacy mpn_refresh_token (fallback when no expiration times)
   * Uses the /token/refresh endpoint with just the refresh token
   */
  private static async refreshWithLegacyToken(
    refreshToken: string
  ): Promise<string | null> {
    try {
      // POST /token/refresh: no request body per API spec
      // Refresh token sent via Authorization header (AdminRefreshAuthorizer)
      const response = await axios.post<TokenRefreshResponse>(
        `${env.VITE_API_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
        undefined,
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const newTokens = response.data;

      // If response includes expiration times, store in new system
      if (
        newTokens.accessToken &&
        newTokens.accessTokenExp &&
        newTokens.refreshToken &&
        newTokens.refreshTokenExp
      ) {
        TokenStorage.setTokens(newTokens);
      }

      // Update legacy storage
      if (newTokens.accessToken) {
        localStorage.setItem('mpn_access_token', newTokens.accessToken);
      }
      if (newTokens.refreshToken) {
        localStorage.setItem('mpn_refresh_token', newTokens.refreshToken);
      }

      return newTokens.accessToken;
    } catch (error) {
      console.error('Token refresh with legacy token failed:', error);

      // Clear legacy tokens on failure
      localStorage.removeItem('mpn_access_token');
      localStorage.removeItem('mpn_refresh_token');

      return null;
    }
  }

  /**
   * Check if token refresh is needed and perform it if necessary
   * This can be called proactively to refresh tokens before they expire
   */
  static async refreshIfNeeded(): Promise<void> {
    if (TokenStorage.isAccessTokenExpiring()) {
      await this.refreshAccessToken();
    }
  }

  /**
   * Get a valid access token, refreshing if necessary
   * This is the recommended way to get an access token
   */
  static async getValidAccessToken(): Promise<string | null> {
    // Try to get existing valid token from TokenStorage
    let token = TokenStorage.getAccessToken();

    if (token) {
      return token;
    }

    // Token is expired or missing, try to refresh using TokenStorage
    if (!TokenStorage.isRefreshTokenExpired()) {
      token = await this.refreshAccessToken();
      if (token) {
        return token;
      }
    }

    // Fallback: Check if we have a legacy refresh token we can use
    const legacyRefreshToken = localStorage.getItem('mpn_refresh_token');
    if (legacyRefreshToken) {
      token = await this.refreshAccessToken();
      return token;
    }

    // Both token systems are invalid
    return null;
  }

  /**
   * Clear all tokens (logout)
   */
  static clearTokens(): void {
    TokenStorage.clearTokens();

    // Also clear legacy tokens
    localStorage.removeItem('mpn_access_token');
    localStorage.removeItem('mpn_refresh_token');
  }
}
